// Layout-primitiv (2026-06-04) — mot innbakte overlapp.
//
// Hele spillet plasserte tekst med hardkodede y-koordinater som antok
// korte tekster. Kombinert med `fontStr`-oppblåsing og variabelt innhold
// (boktitler, navn, wrappet beskrivelse) sprenger det boksene «alltid,
// uansett skjerm». Løsningen er å stable fra faktisk målt teksthøyde i
// stedet for magiske tall.
//
// Kjernen her er bevisst Phaser-fri: den rører bare `.y`/`.height`-feltene,
// så stable-matematikken kan enhetstestes uten canvas. Se docs/superpowers/
// specs/2026-06-04-layout-primitiv-design.md.

/**
 * Minimalt grensesnitt layout-kjernen trenger: et målt `height` og en
 * skrivbar `y`. Phaser-tekst/-container/-bilde med origin (0,0) oppfyller
 * dette. (Origin (0,0) forutsettes for stablede elementer.)
 */
export interface VertikaltElement {
  height: number;
  y: number;
}

/**
 * Vertikal stable-markør. Plasserer elementer topp-mot-bunn med et gap
 * mellom hvert, der hvert y beregnes fra forrige elements målte bunn.
 *
 * Gap legges *mellom* elementer (ikke etter det siste), så `hoyde()` er
 * den faktiske innholdshøyden uten etterfølgende luft.
 *
 * Rører kun `.y` — `.x` er kallerens ansvar (mange stabler deler samme
 * venstre-x, men noen har innrykk). `startX` eksponeres som `.x` for
 * referanse.
 */
export class Kolonne {
  readonly x: number;
  private readonly startY: number;
  private readonly standardGap: number;
  private markor: number;
  private antall = 0;

  constructor(startX: number, startY: number, standardGap = 0) {
    this.x = startX;
    this.startY = startY;
    this.standardGap = standardGap;
    this.markor = startY;
  }

  /**
   * Setter `obj.y` til løpende markør (etter gap for alle bortsett fra
   * det første elementet), og flytter markøren til objektets bunn.
   * Returnerer objektet for kjeding.
   */
  legg<T extends VertikaltElement>(obj: T, gap?: number): T {
    if (this.antall > 0) {
      this.markor += gap ?? this.standardGap;
    }
    obj.y = this.markor;
    this.markor += obj.height;
    this.antall += 1;
    return obj;
  }

  /** Flytter markøren ned uten å plassere noe (eksplisitt mellomrom). */
  hoppOver(px: number): void {
    this.markor += px;
  }

  /** Y-koordinat for bunnen av innholdet så langt. */
  bunn(): number {
    return this.markor;
  }

  /** Samlet høyde fra startY til siste bunn (uten etterfølgende gap). */
  hoyde(): number {
    return this.markor - this.startY;
  }
}

/** Boks-høyde som rommer `innholdHoyde` med `padding` over og under. */
export function boksHoyde(innholdHoyde: number, padding: number): number {
  return innholdHoyde + padding * 2;
}
